import { flow, makeAutoObservable } from 'mobx';

import { EntitiesFetchingStatus } from '../../utilities/apiCommunication';

import type { GetAllowedTabsRequest, GetAllowedTabsResponse } from '../../server/PatientPortraitApi/ProductContextApi';
import type { NavinaPatientId } from '@navina/api-types';

interface CancellablePromise<T> extends Promise<T> {
	readonly cancel: VoidFunction;
}

type GetAllowedTabs = (requestPayload: GetAllowedTabsRequest) => Promise<GetAllowedTabsResponse>;

export interface IAllowedTabsModel {
	readonly tabs: ReadonlyArray<string> | null;
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly initializeFetchAllowedTabs: VoidFunction;
	readonly dispose: VoidFunction;
}

export interface AllowedTabsModelParams {
	readonly patientId: NavinaPatientId;
	readonly maybeAppointmentId: string | undefined;
	readonly maybeSkipValidation: 'true' | 'false' | undefined;
	readonly getAllowedTabs: GetAllowedTabs;
}

function* fetchAllowedTabsFlowGenerator(this: AllowedTabsModel) {
	const { patientId, maybeAppointmentId, maybeSkipValidation, getAllowedTabs, setFetchingStatus, setAllowedTabsData } =
		this;
	setFetchingStatus(EntitiesFetchingStatus.Pending);

	try {
		const response: Awaited<ReturnType<AllowedTabsModel['getAllowedTabs']>> = yield getAllowedTabs({
			patientId,
			appointmentId: maybeAppointmentId,
			skipValidation: maybeSkipValidation,
		});

		setAllowedTabsData(response);
		setFetchingStatus(EntitiesFetchingStatus.Success);
	} catch (error) {
		console.error('Error while fetching allowed tabs', error);
		setFetchingStatus(EntitiesFetchingStatus.Error);
	}
}

export class AllowedTabsModel implements IAllowedTabsModel {
	readonly patientId: AllowedTabsModelParams['patientId'];
	readonly maybeAppointmentId: AllowedTabsModelParams['maybeAppointmentId'];
	readonly maybeSkipValidation: AllowedTabsModelParams['maybeSkipValidation'];
	readonly getAllowedTabs: GetAllowedTabs;

	maybeAllowedTabsData: GetAllowedTabsResponse | null = null;
	allowedTabsFetchingStatus: EntitiesFetchingStatus = EntitiesFetchingStatus.Idle;

	private constructor({ patientId, maybeAppointmentId, maybeSkipValidation, getAllowedTabs }: AllowedTabsModelParams) {
		this.patientId = patientId;
		this.maybeAppointmentId = maybeAppointmentId;
		this.maybeSkipValidation = maybeSkipValidation;
		this.getAllowedTabs = getAllowedTabs;

		makeAutoObservable(this);
	}

	static readonly create = (params: AllowedTabsModelParams): AllowedTabsModel => {
		return new AllowedTabsModel(params);
	};

	static readonly fetchAllowedTabs = async (params: AllowedTabsModelParams): Promise<ReadonlyArray<string>> => {
		const response = await params.getAllowedTabs({
			patientId: params.patientId,
			appointmentId: params.maybeAppointmentId,
			skipValidation: params.maybeSkipValidation,
		});

		return response.tabs;
	};

	readonly initializeFetchAllowedTabs = (): void => {
		this.fetchAllowedTabs();
	};

	readonly dispose = (): void => {
		this._ongoingFetchAllowedTabsFlow?.cancel();
		this._ongoingFetchAllowedTabsFlow = null;
		this.setAllowedTabsData(null);
		this.setFetchingStatus(EntitiesFetchingStatus.Idle);
	};

	readonly setFetchingStatus = (status: typeof this.allowedTabsFetchingStatus): void => {
		this.allowedTabsFetchingStatus = status;
	};

	readonly setAllowedTabsData = (data: typeof this.maybeAllowedTabsData): void => {
		this.maybeAllowedTabsData = data;
	};

	get tabs(): IAllowedTabsModel['tabs'] {
		return this.maybeAllowedTabsData?.tabs ?? null;
	}

	get isLoading(): boolean {
		return (
			this.allowedTabsFetchingStatus === EntitiesFetchingStatus.Idle ||
			this.allowedTabsFetchingStatus === EntitiesFetchingStatus.Pending
		);
	}

	get isError(): boolean {
		return this.allowedTabsFetchingStatus === EntitiesFetchingStatus.Error;
	}

	_ongoingFetchAllowedTabsFlow: CancellablePromise<void> | null = null;
	readonly fetchAllowedTabs = (): void => {
		this._ongoingFetchAllowedTabsFlow?.cancel();
		this._ongoingFetchAllowedTabsFlow = this.fetchAllowedTabsFlow();
	};

	readonly fetchAllowedTabsFlow = flow(fetchAllowedTabsFlowGenerator);
}
